import styled from '@emotion/styled'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { useCustomer } from 'api/react-query'
import dayjs from 'dayjs'
import useMaterialNotification from 'hooks/useMaterialNotification.tsx'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import Colors from '../../../figma/panda/Colors'
import { useCloseFraudCase, useMarkAsNotified, useMarkTransactionAsFraud, useMarkTransactionAsNotFraud } from '../api/useFraudCaseMutations'
import { useFraudCaseDetails } from '../api/useFraudCases'
import type { FraudCase, FraudPurchaseTransaction } from '../types'
import { buildNotificationText } from './notificationTemplates'

const StyledLink = styled(Link)`
  color: ${Colors.brandBlueCTA};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`

const statusChipColor: Record<string, { bg: string; color: string }> = {
  NEW: { bg: Colors.bluePrimary100, color: Colors.bluePrimary700 },
  NOTIFIED: { bg: Colors.alert100, color: Colors.alert600 },
  CLOSED: { bg: Colors.base300, color: Colors.base500 },
}

const resolutionChipColor: Record<string, { bg: string; color: string }> = {
  PENDING: { bg: Colors.alert100, color: Colors.alert600 },
  FRAUD: { bg: Colors.notificationErrors100, color: Colors.notificationErrors600 },
  NOT_FRAUD: { bg: Colors.success100, color: Colors.success700 },
}

type FraudCaseDetailPanelProps = {
  fraudCase: FraudCase
}

const isPurchaseTransaction = (tx: FraudCase['transactions'][number]): tx is FraudPurchaseTransaction => tx.transactionType === 'PURCHASE'

const NotificationTemplateSection = ({ notificationText }: { notificationText: string }) => {
  const [expanded, setExpanded] = useState(false)

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="subtitle1">Notification Template</Typography>
        <Button variant="text" size="small" onClick={() => setExpanded((prev) => !prev)}>
          {expanded ? 'Hide' : 'Show'}
        </Button>
      </Box>
      {expanded && (
        <>
          <Box
            component="pre"
            data-testid="notification-template"
            sx={{
              p: 2,
              backgroundColor: Colors.base100,
              borderRadius: 1,
              whiteSpace: 'pre-wrap',
              fontFamily: 'monospace',
              fontSize: 13,
              mb: 1,
              m: 0,
            }}
          >
            {notificationText}
          </Box>
          <Button variant="outlined" size="small" onClick={() => navigator.clipboard.writeText(notificationText)}>
            Copy
          </Button>
        </>
      )}
    </Box>
  )
}

export const FraudCaseDetailPanel = ({ fraudCase }: FraudCaseDetailPanelProps) => {
  const notify = useMaterialNotification()
  const markAsNotified = useMarkAsNotified()
  const closeFraudCase = useCloseFraudCase()
  const markTransactionAsFraud = useMarkTransactionAsFraud()
  const markTransactionAsNotFraud = useMarkTransactionAsNotFraud()
  const { data: details, isLoading: detailsLoading } = useFraudCaseDetails(fraudCase.id)
  const { data: customer, isLoading: customerLoading } = useCustomer(fraudCase.customerId)

  const [expandedTransactions, setExpandedTransactions] = useState<Set<string>>(new Set())
  const hasPendingTransactions = fraudCase.transactions.some((tx) => tx.resolution === 'PENDING')
  const notificationText =
    details?.cardLastFourDigits && fraudCase.status === 'NEW' && !customerLoading
      ? buildNotificationText(
          { fraudCase, cardholderName: details.cardholderName, cardLastFourDigits: details.cardLastFourDigits },
          customer?.cardPartner,
          customer?.country,
        )
      : null

  return (
    <Box sx={{ p: 3, borderBottom: '2px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Typography variant="h6" noWrap>
          Case {fraudCase.id}
        </Typography>
        <Chip
          label={fraudCase.status}
          size="small"
          data-testid="case-status-chip"
          sx={{
            backgroundColor: statusChipColor[fraudCase.status]?.bg,
            color: statusChipColor[fraudCase.status]?.color,
            fontWeight: 600,
          }}
        />
        <Chip
          label={fraudCase.resolution}
          size="small"
          data-testid="case-resolution-chip"
          sx={{
            backgroundColor: resolutionChipColor[fraudCase.resolution]?.bg,
            color: resolutionChipColor[fraudCase.resolution]?.color,
            fontWeight: 600,
          }}
        />
        <Box sx={{ flex: 1 }} />
        {fraudCase.status === 'NEW' && (
          <Button
            variant="outlined"
            onClick={() =>
              markAsNotified.mutate(fraudCase.id, {
                onSuccess: () =>
                  notify(details?.cardholderName ? `Case belonging to ${details.cardholderName} marked as notified` : 'Marked as notified'),
              })
            }
            disabled={markAsNotified.isLoading}
          >
            Mark as Notified
          </Button>
        )}
        {fraudCase.status !== 'CLOSED' && (
          <Tooltip title={hasPendingTransactions ? 'All transactions must be resolved before closing' : ''}>
            <span>
              <Button
                variant="contained"
                color="error"
                onClick={() =>
                  closeFraudCase.mutate(fraudCase.id, {
                    onSuccess: () =>
                      notify(details?.cardholderName ? `Fraud case closed for ${details.cardholderName}` : 'Fraud case closed'),
                  })
                }
                disabled={hasPendingTransactions || closeFraudCase.isLoading}
              >
                Close Case
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>

      {(detailsLoading || customerLoading) && <CircularProgress size={24} sx={{ mb: 2 }} />}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
        <Typography variant="body2">
          <Box component="span" sx={{ display: 'inline-block', minWidth: 160 }}>
            Customer:
          </Box>
          <StyledLink to={`/customers/${fraudCase.customerId}`}>{fraudCase.customerId}</StyledLink>
        </Typography>
        {details && (
          <Typography variant="body2">
            <Box component="span" sx={{ display: 'inline-block', minWidth: 160 }}>
              Customer Name:
            </Box>
            {details.customerName}
          </Typography>
        )}
        <Typography variant="body2">
          <Box component="span" sx={{ display: 'inline-block', minWidth: 160 }}>
            Person:
          </Box>
          {fraudCase.personId}
        </Typography>
        {details && (
          <Typography variant="body2">
            <Box component="span" sx={{ display: 'inline-block', minWidth: 160 }}>
              Name:
            </Box>
            {details.cardholderName}
          </Typography>
        )}
        <Typography variant="body2">
          <Box component="span" sx={{ display: 'inline-block', minWidth: 160 }}>
            Card:
          </Box>
          {fraudCase.cardId}
        </Typography>
        {details && (
          <Typography variant="body2">
            <Box component="span" sx={{ display: 'inline-block', minWidth: 160 }}>
              Card Number:
            </Box>
            {details.cardLastFourDigits || '-'}
          </Typography>
        )}
        {fraudCase.externalId && (
          <Typography variant="body2">
            <Box component="span" sx={{ display: 'inline-block', minWidth: 160 }}>
              External Id:
            </Box>
            {fraudCase.externalId}
          </Typography>
        )}
        {details && (
          <Typography variant="body2">
            <Box component="span" sx={{ display: 'inline-block', minWidth: 160 }}>
              Email:
            </Box>
            {details.cardholderEmail || '-'}
          </Typography>
        )}
        {customer && (
          <Typography variant="body2">
            <Box component="span" sx={{ display: 'inline-block', minWidth: 160 }}>
              Card Partner:
            </Box>
            {customer.cardPartner}
          </Typography>
        )}
        {customer && (
          <Typography variant="body2">
            <Box component="span" sx={{ display: 'inline-block', minWidth: 160 }}>
              Country:
            </Box>
            {customer.country}
          </Typography>
        )}
      </Box>

      {notificationText && <NotificationTemplateSection notificationText={notificationText} />}

      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Transactions
      </Typography>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Original Amount</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Resolution</TableCell>
              {fraudCase.status !== 'CLOSED' && <TableCell>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {fraudCase.transactions.map((tx) => (
              <TableRow
                key={tx.transactionId}
                data-testid={`transaction-row-${tx.transactionId}`}
                sx={tx.resolution === 'PENDING' ? { backgroundColor: Colors.alert100 } : undefined}
              >
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{tx.transactionId}</TableCell>
                <TableCell>{tx.transactionType}</TableCell>
                <TableCell>{tx.description}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {isPurchaseTransaction(tx) && tx.originalAmount ? `${tx.originalAmount.value} ${tx.originalAmount.currency}` : '-'}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {isPurchaseTransaction(tx) ? `${tx.amount.value} ${tx.amount.currency}` : '-'}
                </TableCell>
                <TableCell>{isPurchaseTransaction(tx) ? tx.status : '-'}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{dayjs(tx.transactionDate).format('YYYY-MM-DD HH:mm')}</TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    component="span"
                    data-testid={`transaction-resolution-${tx.transactionId}`}
                    sx={{
                      color: resolutionChipColor[tx.resolution]?.color,
                      fontWeight: 600,
                    }}
                  >
                    {tx.resolution}
                  </Typography>
                </TableCell>
                {fraudCase.status !== 'CLOSED' && (
                  <TableCell>
                    {(tx.resolution === 'PENDING' || expandedTransactions.has(tx.transactionId)) && (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          color="error"
                          data-testid={`${tx.transactionId}-mark-fraud`}
                          onClick={() => {
                            markTransactionAsFraud.mutate({ caseId: fraudCase.id, transactionId: tx.transactionId })
                            setExpandedTransactions((prev) => {
                              const next = new Set(prev)
                              next.delete(tx.transactionId)
                              return next
                            })
                          }}
                        >
                          Fraud
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          data-testid={`${tx.transactionId}-mark-not-fraud`}
                          onClick={() => {
                            markTransactionAsNotFraud.mutate({ caseId: fraudCase.id, transactionId: tx.transactionId })
                            setExpandedTransactions((prev) => {
                              const next = new Set(prev)
                              next.delete(tx.transactionId)
                              return next
                            })
                          }}
                        >
                          Not Fraud
                        </Button>
                      </Box>
                    )}
                    {tx.resolution !== 'PENDING' && !expandedTransactions.has(tx.transactionId) && (
                      <Button
                        size="small"
                        variant="text"
                        color="inherit"
                        data-testid={`${tx.transactionId}-change`}
                        onClick={() => setExpandedTransactions((prev) => new Set(prev).add(tx.transactionId))}
                        sx={{ color: Colors.base500, textTransform: 'none' }}
                      >
                        Change
                      </Button>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  )
}
